import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface Teacher {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  status?: string; // active, inactive
  permissions?: string[]; // list of granted keys
  assigned_classes: string | null;
}

const ManageTeachers: React.FC = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  
  // Form State
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('teacher');
  const [status, setStatus] = useState('active');
  
  // Permission Toggles
  const [canEnterScores, setCanEnterScores] = useState(true);
  const [canValidate, setCanValidate] = useState(false);
  const [canPrint, setCanPrint] = useState(true);

  const fetchTeachers = async () => {
    try {
      const res = await api.get('/teachers');
      if (res.data.success) {
        // Enriched mock data matching SaaS schema
        const data = res.data.data.map((t: any) => ({
          ...t,
          status: t.status || 'active',
          permissions: t.permissions || ['enter_scores', 'print_reports']
        }));
        setTeachers(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const handleOpenModal = (teacher?: Teacher) => {
    if (teacher) {
      setEditingTeacher(teacher);
      setFirstName(teacher.first_name);
      setLastName(teacher.last_name);
      setEmail(teacher.email);
      setRole(teacher.role);
      setStatus(teacher.status || 'active');
      setCanEnterScores(teacher.permissions?.includes('enter_scores') ?? true);
      setCanValidate(teacher.permissions?.includes('validate_scores') ?? false);
      setCanPrint(teacher.permissions?.includes('print_reports') ?? true);
      setPassword('');
    } else {
      setEditingTeacher(null);
      setFirstName('');
      setLastName('');
      setEmail('');
      setRole('teacher');
      setStatus('active');
      setCanEnterScores(true);
      setCanValidate(false);
      setCanPrint(true);
      setPassword('');
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Assemble permissions array
    const permissions: string[] = [];
    if (canEnterScores) permissions.push('enter_scores');
    if (canValidate) permissions.push('validate_scores');
    if (canPrint) permissions.push('print_reports');

    const payload = { 
      first_name: firstName, 
      last_name: lastName, 
      email, 
      role, 
      status,
      permissions,
      password 
    };
    
    try {
      if (editingTeacher) {
        await api.put(`/teachers/${editingTeacher.id}`, payload);
      } else {
        await api.post('/teachers', payload);
      }
      setIsModalOpen(false);
      fetchTeachers();
    } catch (err) {
      console.error("Error saving teacher", err);
      alert("Failed to save teacher.");
    }
  };

  const toggleStatus = async (teacher: Teacher) => {
    const nextStatus = teacher.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/teachers/${teacher.id}`, {
        ...teacher,
        status: nextStatus
      });
      fetchTeachers();
    } catch (err) {
      alert("Failed to update teacher account status.");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to remove this teacher account?")) return;
    try {
      await api.delete(`/teachers/${id}`);
      fetchTeachers();
    } catch (err) {
      console.error("Error deleting teacher", err);
      alert("Failed to delete teacher.");
    }
  };

  // Filter teachers based on search
  const filteredTeachers = teachers.filter(t => 
    `${t.first_name} ${t.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      
      {/* Page Header */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-page-header mb-1">Staff & Accounts Management</h1>
          <p className="text-helper">Administer school user access, adjust permissions, activate/deactivate accounts, and edit teaching allocations.</p>
        </div>
        <button 
          onClick={() => handleOpenModal()} 
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl transition-all shadow-sm text-nav text-center whitespace-nowrap"
        >
          + Create Account
        </button>
      </div>

      {/* Filter and Search Drawer */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative w-full sm:max-w-xs">
          <input 
            type="text" 
            placeholder="Search by name, email, or role..." 
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-table-body focus:ring-1 focus:ring-indigo-500 focus:bg-white outline-none transition-all"
          />
          <svg className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          Showing {filteredTeachers.length} of {teachers.length} Accounts
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent"></div>
          </div>
        ) : (
          <>
            {/* A. Responsive Card view for Mobile viewports */}
            <div className="block md:hidden p-4 space-y-3.5">
              {filteredTeachers.length === 0 ? (
                <p className="text-center text-helper italic text-slate-400 py-6">No users found matching query.</p>
              ) : (
                filteredTeachers.map((t) => (
                  <div key={t.id} className="p-4 rounded-xl border border-slate-150 bg-white space-y-3 shadow-sm">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-card-title text-slate-900">{t.first_name} {t.last_name}</span>
                        <button 
                          onClick={() => toggleStatus(t)}
                          className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border transition-colors ${
                            t.status === 'active' 
                              ? 'bg-emerald-50 border-emerald-250 text-emerald-700' 
                              : 'bg-rose-50 border-rose-250 text-rose-700'
                          }`}
                        >
                          {t.status || 'active'}
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-500">{t.email}</p>
                    </div>

                    <div className="flex flex-wrap gap-1 pt-1">
                      <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider">
                        Role: {t.role.replace('_', ' ')}
                      </span>
                      {t.permissions?.map(p => (
                        <span key={p} className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-[8px] font-semibold border border-indigo-100">
                          {p.replace('_', ' ')}
                        </span>
                      ))}
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                      <div className="text-[9px]">
                        <span className="text-slate-400 block font-bold uppercase tracking-wider">Assigned Classes</span>
                        {t.assigned_classes ? (
                          <span className="text-indigo-600 font-bold">{t.assigned_classes}</span>
                        ) : (
                          <span className="text-slate-400 italic">None</span>
                        )}
                      </div>
                      
                      <div className="flex gap-3 text-nav">
                        <button onClick={() => handleOpenModal(t)} className="text-indigo-600 font-semibold hover:underline">Edit</button>
                        <button onClick={() => handleDelete(t.id)} className="text-rose-500 font-semibold hover:underline">Remove</button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* B. Wide Table view for Desktop/Tablet viewports */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Name</th>
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Email</th>
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Role</th>
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Assigned Classes</th>
                    <th className="p-4 text-center text-card-title text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-150">
                  {filteredTeachers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-helper italic text-slate-400">No users found.</td>
                    </tr>
                  ) : (
                    filteredTeachers.map((t) => (
                      <tr key={t.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="p-4 text-table-body font-bold text-slate-900">{t.first_name} {t.last_name}</td>
                        <td className="p-4 text-table-body text-slate-500">{t.email}</td>
                        <td className="p-4 text-table-body capitalize">{t.role.replace('_', ' ')}</td>
                        <td className="p-4">
                          <button 
                            onClick={() => toggleStatus(t)}
                            className={`inline-block px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border transition-colors ${
                              t.status === 'active' 
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                                : 'bg-rose-50 border-rose-200 text-rose-700'
                            }`}
                          >
                            {t.status || 'active'}
                          </button>
                        </td>
                        <td className="p-4">
                          {t.assigned_classes ? (
                            <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border border-indigo-150">{t.assigned_classes}</span>
                          ) : (
                            <span className="text-slate-400 italic text-[10px]">Unassigned</span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <button onClick={() => handleOpenModal(t)} className="text-indigo-600 font-semibold hover:underline mr-4 text-nav">Edit</button>
                          <button onClick={() => handleDelete(t.id)} className="text-rose-500 font-semibold hover:underline text-nav">Remove</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal Form */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-slate-100">
            <h2 className="text-section-header mb-4">{editingTeacher ? 'Modify User Profile' : 'Register User Account'}</h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-form-label uppercase tracking-wider block">First Name</label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required />
                </div>
                <div className="space-y-1">
                  <label className="text-form-label uppercase tracking-wider block">Last Name</label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Email Address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-form-label uppercase tracking-wider block">Institutional Role</label>
                  <select value={role} onChange={e => setRole(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all">
                    <option value="teacher">Regular Teacher</option>
                    <option value="class_teacher">Class Teacher / Tutor</option>
                    <option value="headteacher">Head Teacher</option>
                    <option value="school_admin">School Administrator</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-form-label uppercase tracking-wider block">Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all">
                    <option value="active">Active</option>
                    <option value="inactive">Suspended</option>
                  </select>
                </div>
              </div>

              {/* Functional Permission Toggles */}
              <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-100 space-y-2">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Security Permissions</span>
                <div className="space-y-2 text-nav">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={canEnterScores} onChange={e => setCanEnterScores(e.target.checked)} className="rounded border-slate-350 text-indigo-650 focus:ring-indigo-500" />
                    <span className="font-semibold text-slate-700">Enter Continuous Assessment Scores</span>
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={canValidate} onChange={e => setCanValidate(e.target.checked)} className="rounded border-slate-350 text-indigo-650 focus:ring-indigo-500" />
                    <span className="font-semibold text-slate-700">Validate & Lock Cohort Results</span>
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={canPrint} onChange={e => setCanPrint(e.target.checked)} className="rounded border-slate-350 text-indigo-650 focus:ring-indigo-500" />
                    <span className="font-semibold text-slate-700">Print Printable Report Cards</span>
                  </label>
                </div>
              </div>

              {!editingTeacher && (
                <div className="space-y-1">
                  <label className="text-form-label uppercase tracking-wider block">Initial Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required={!editingTeacher} />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-nav font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                <button type="submit" className="px-5 py-2 text-nav font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-sm transition-colors">Save Account</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTeachers;
